import { durationInDays, formatDate, formatDayMonth } from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoIntervalo,
  CargaTrabalhoProfissional,
} from "@/modules/carga-trabalho";
import { isObraCancelada } from "@/modules/recursos";
import type { ConflitoFornecedor, ObraEscopoMacro } from "@/modules/recursos";

export function plural(total: number, singular: string, pluralForm: string): string {
  return `${total} ${total === 1 ? singular : pluralForm}`;
}

/** Interseção entre dois intervalos fechados (`yyyy-MM-dd`); `null` quando não se tocam. */
export function intersecao(
  a: CargaTrabalhoIntervalo,
  b: CargaTrabalhoIntervalo,
): CargaTrabalhoIntervalo | null {
  const inicio = a.inicio > b.inicio ? a.inicio : b.inicio;
  const fim = a.fim < b.fim ? a.fim : b.fim;
  return inicio <= fim ? { inicio, fim } : null;
}

/** `19 dias` / `1 dia` — dias da sobreposição do conflito. */
export function diasDoConflito(conflito: ConflitoFornecedor): number {
  return durationInDays(conflito);
}

/** `08/06 a 26/06`, como no pill do resumo. */
export function intervaloCurto(intervalo: CargaTrabalhoIntervalo): string {
  return `${formatDayMonth(intervalo.inicio)} a ${formatDayMonth(intervalo.fim)}`;
}

/** `08/06 - 26/06/2026 (19 dias corridos)`, como na seleção do conflito. */
export function descreverSobreposicao(conflito: ConflitoFornecedor): string {
  const dias = durationInDays(conflito);
  return `${formatDayMonth(conflito.inicio)} - ${formatDate(conflito.fim)} (${dias} ${
    dias === 1 ? "dia corrido" : "dias corridos"
  })`;
}

/** O escopo macro está em conflito quando seu período toca a sobreposição. */
export function escopoEmConflito(
  escopo: Pick<ObraEscopoMacro, "inicio" | "fim">,
  conflito: ConflitoFornecedor,
): boolean {
  if (!escopo.inicio || !escopo.fim) return false;
  return intersecao(escopo, conflito) !== null;
}

export interface GeraConflito {
  obra: CargaTrabalhoAlocacao["obra"];
  dias: number;
}

/**
 * "Gera conflito" (Figma): o candidato já tem alocação, em outra obra, que se sobrepõe ao
 * período da alocação remanejada. Devolve a maior sobreposição; `null` quando não há.
 */
export function calcularGeraConflito(
  candidato: CargaTrabalhoProfissional | undefined,
  alocacao: CargaTrabalhoAlocacao,
): GeraConflito | null {
  let melhor: GeraConflito | null = null;
  for (const outra of candidato?.alocacoes ?? []) {
    if (outra.obra.id === alocacao.obra.id || isObraCancelada(outra.obra.situacao)) continue;
    const trecho = intersecao(outra, alocacao);
    if (!trecho) continue;
    const dias = durationInDays(trecho);
    if (!melhor || dias > melhor.dias) melhor = { obra: outra.obra, dias };
  }
  return melhor;
}

/** `a Obra 25 e a Obra 33` / `a Obra 25, a Obra 33 e a Obra 24` (sem repetir obras). */
export function listarObras(obras: { id: string; codigo: string }[]): string {
  const unicas = obras
    .filter((obra, index, lista) => lista.findIndex((outra) => outra.id === obra.id) === index)
    .map((obra) => `a Obra ${obra.codigo}`);
  if (unicas.length <= 1) return unicas.join("");
  return `${unicas.slice(0, -1).join(", ")} e ${unicas.at(-1)}`;
}

/** `duas` / `3` / `4`: a quantidade de obras do conflito nos textos (sem fixar em duas, HU 2041). */
export function quantidadeDeObras(total: number): string {
  return total === 2 ? "duas" : String(total);
}
